import {resolveAgentViewQueueFilters} from "~/utilities/dayPlan/agentViewDayPlanEventContainerQuery";
import type {CapabilityUdoColumnResolver} from "~/utilities/capabilityUdoTemplateColumns";
import type {EventContainerCsvDownloadRequest, EventCsvDownloadRequest,
    UsersCsvDownloadRequest, InvitationsCsvDownloadRequest, TransferCsvDownloadRequest,
    AgentViewDayPlanCsvDownloadRequest, DuluxDayPlanCsvDownloadRequest,
    FailedTransferItemsCsvDownloadRequest, AmaronTrackerCsvDownloadRequest} from "@vesta/vos-types/modules/workspaceLevel/domain/CsvDownload";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {OttopilotColumn} from "~/components/ottopilotDataViewer/column";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import type {ComplexFilter} from "@vesta/vos-types/modules/workspaceLevel/domain/ComplexFilter";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import type {FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import type {OttopilotEventContainerTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTemplate";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";

import {AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID, AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID, AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/EventContainers";
import {AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID, AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID, AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID,
    AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID, AmaronEventTypes} from "@vesta/vos-types/modules/workspaceLevel/workspaceSpecific/domain/amaron/Events";
import {USER_IDENTITY_NAME_VIRTUAL_COLUMN_ID, USER_IDENTITY_EMAIL_VIRTUAL_COLUMN_ID, USER_IDENTITY_PHONE_NUMBER_VIRTUAL_COLUMN_ID} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserIdentityVirtualColumnIds";
import {CSV_DOWNLOAD_LIMITS, eventContainerCsvDownloadRequestValidator, eventCsvDownloadRequestValidator,
    usersCsvDownloadRequestValidator, invitationsCsvDownloadRequestValidator, transferCsvDownloadRequestValidator,
    agentViewDayPlanCsvDownloadRequestValidator, duluxDayPlanCsvDownloadRequestValidator,
    failedTransferItemsCsvDownloadRequestValidator, amaronTrackerCsvDownloadRequestValidator} from "@vesta/vos-types/modules/workspaceLevel/domain/CsvDownload";

export type NativeCsvRequestByPath = {
    "/ottopilot/event-containers/export": EventContainerCsvDownloadRequest;
    "/ottopilot/events/export": EventCsvDownloadRequest;
    "/users/export": UsersCsvDownloadRequest;
    "/private-invites/export": InvitationsCsvDownloadRequest;
    "/event-container-movements/export": TransferCsvDownloadRequest;
    "/api/workspace-specific/amaron/disposition-tracker/export": AmaronTrackerCsvDownloadRequest;
    "/agent-view/day-plan/event-containers/export": AgentViewDayPlanCsvDownloadRequest;
    "/dulux/agent-view/day-plan/export": DuluxDayPlanCsvDownloadRequest;
    "/event-container-movements/failed-operation-items/export": FailedTransferItemsCsvDownloadRequest;
};
export type NativeCsvDownloadPath = keyof NativeCsvRequestByPath;
type NativeCsvRequestKey = {[P in NativeCsvDownloadPath]: keyof NativeCsvRequestByPath[P]}[NativeCsvDownloadPath];
type NativeCsvQueryFields<P extends NativeCsvDownloadPath> = Omit<NativeCsvRequestByPath[P],
    "workspaceId" | "userId" | "columns" | "selectedIds">;
export type NativeCsvQuery<P extends NativeCsvDownloadPath> = NativeCsvQueryFields<P> & {
    [K in Exclude<NativeCsvRequestKey, keyof NativeCsvQueryFields<P>>]?: never;
};

/** Conservative browser/gateway request-target guard; the shared backend body limit remains independent. */
export const NATIVE_CSV_DOWNLOAD_MAX_URL_BYTES = 8 * 1024;

const validators = {
    "/ottopilot/event-containers/export": eventContainerCsvDownloadRequestValidator,
    "/ottopilot/events/export": eventCsvDownloadRequestValidator,
    "/users/export": usersCsvDownloadRequestValidator,
    "/private-invites/export": invitationsCsvDownloadRequestValidator,
    "/event-container-movements/export": transferCsvDownloadRequestValidator,
    "/api/workspace-specific/amaron/disposition-tracker/export": amaronTrackerCsvDownloadRequestValidator,
    "/agent-view/day-plan/event-containers/export": agentViewDayPlanCsvDownloadRequestValidator,
    "/dulux/agent-view/day-plan/export": duluxDayPlanCsvDownloadRequestValidator,
    "/event-container-movements/failed-operation-items/export": failedTransferItemsCsvDownloadRequestValidator,
};

/** Native CSV configuration is independent of the legacy stream transport. */
export type NativeCsvDownloadConfig<T extends {id: Uuid}> = {
    baseUrl: string;
    workspaceId: Uuid;
    userId: Uuid;
    resolveColumnId: (column: OttopilotColumn<T>) => string | null;
    /** Source-backed identity mapping for the old Event translators' null predicates. */
    nullFilterColumnId?: (id: string) => string;
} & {[P in NativeCsvDownloadPath]: {
    path: P;
    resolveQuery: (controller: OttopilotDataViewerController<T>) => NativeCsvQuery<P>;
}}[NativeCsvDownloadPath];

/** Repair only demonstrated null predicates from the selected caller, never an
 * arbitrary '?' operation. All other leaves and mandatory groups are unchanged. */
export function nativeCsvNullFilters(filters: ComplexFilter, configuration: FilterConfiguration, normalize: (id: string) => string): ComplexFilter {
    const pending = new Map<string, Array<string>>();
    for (const item of configuration.lineItems) {
        if (item.operation !== FilterOperation.isNull && item.operation !== FilterOperation.isNotNull) {continue;}
        const id = normalize(item.columnId);
        const operations = pending.get(id) ?? [];
        operations.push(item.operation === FilterOperation.isNull ? "isNull" : "isNotNull");
        pending.set(id, operations);
    }
    const visit = (group: ComplexFilter): ComplexFilter => ({...group, filters: group.filters.map((item) => {
        if ("filters" in item) {return visit(item);}
        if (item.operator !== "?") {return item;}
        const operator = pending.get(item.column)?.shift();
        if (operator === undefined) {throw new Error("This filter has no supported CSV translation. Remove or change the filter and retry.");}
        return {...item, operator: operator, value: null};
    })});
    return visit(filters);
}

export function prepareNativeCsvDownload<T extends {id: Uuid}>(args: {
    environment: unknown;
    config: NativeCsvDownloadConfig<T>;
    controller: OttopilotDataViewerController<T>;
    columnIsExportable: (column: OttopilotColumn<T>) => boolean;
    selectedIds?: ReadonlyArray<Uuid>;
    selectedRowId?: Uuid;
}): string {
    const {config} = args;
    if (!isNativeCsvEnvironment(args.environment)) {
        throw new Error("Browser CSV downloads are currently available only in development and staging.");
    }
    const columns: Array<string> = [];
    for (const column of args.controller.columns.lineItems.filter(args.columnIsExportable)) {
        const id = config.resolveColumnId(column);
        if (id === null) {throw new Error(`CSV export does not support the “${column.displayName}” column yet.`);}
        if (!columns.includes(id)) {columns.push(id);}
    }
    if (columns.length === 0) {throw new Error("No exportable columns for the current view.");}
    const selectedIds = args.selectedIds ?? (args.selectedRowId !== undefined && config.path === "/users/export" ? [args.selectedRowId] : undefined);
    if (args.selectedIds !== undefined && config.path !== "/users/export") {
        throw new Error("Exact row selection is supported only for Users exports.");
    }
    const query = selectedIds === undefined ? config.resolveQuery(args.controller) : {};
    if (selectedIds === undefined) {
        // Exact-row exports retain the current filter/scope, but intentionally
        // discard the table sort below; an unrepresentable table sort must not
        // prevent exporting a single known row.
        if (args.selectedRowId === undefined && (query.orderBy?.length ?? 0) < args.controller.sortConfiguration.lineItems.length) {
            throw new Error("The current sort cannot be represented in this CSV export.");
        }
    }
    let filters = query.filters;
    let orderBy = query.orderBy;
    if (args.selectedRowId !== undefined && config.path !== "/users/export") {
        if (config.path !== "/dulux/agent-view/day-plan/export") {
            const rowFilter: ComplexFilter = {logic: "AND", filters: [{column: "id", operator: "equals", value: args.selectedRowId}]};
            filters = filters === undefined ? rowFilter : {logic: "AND", filters: [filters, rowFilter]};
        }
        orderBy = undefined;
    }
    const queueFilters = config.path === "/agent-view/day-plan/event-containers/export"
        ? resolveAgentViewQueueFilters(filters) : undefined;
    if (queueFilters !== undefined) {
        filters = queueFilters.complexFilters;
    }
    // Endpoint-specific query fields are supplied by its typed configuration;
    // strict shared validators remain the final wire-contract boundary.
    return buildNativeCsvDownloadUrl({
        baseUrl: config.baseUrl,
        path: config.path,
        request: {
            ...query,
            ...(queueFilters?.leadCategory === undefined ? {} : {leadCategory: queueFilters.leadCategory}),
            filters: undefined,
            orderBy: undefined,
            workspaceId: config.workspaceId, userId: config.userId, columns: columns,
            ...(selectedIds === undefined && filters !== undefined ? {filters: config.nullFilterColumnId === undefined ? filters :
                nativeCsvNullFilters(filters, args.controller.filterConfiguration, config.nullFilterColumnId)} : {}),
            ...(selectedIds === undefined && orderBy !== undefined ? {orderBy: orderBy} : {}),
            ...(args.selectedRowId !== undefined && (config.path === "/dulux/agent-view/day-plan/export" ||
                config.path === "/agent-view/day-plan/event-containers/export") ? {rowId: args.selectedRowId} : {}),
            ...(selectedIds === undefined ? {} : {selectedIds: [...selectedIds]}),
        },
    });
}

export function isNativeCsvEnvironment(_environment: unknown): boolean {
    // return environment === "dev" || environment === "staging";
    return true;
}

export function nativeCsvDownloadUrlIsWithinByteLimit(url: string): boolean {
    return new TextEncoder().encode(url).byteLength <= NATIVE_CSV_DOWNLOAD_MAX_URL_BYTES;
}

/** Users' configured fields must exist in actual template metadata, not merely a UI column map. */
export function nativeCsvUserColumn(id: string, templates: ReadonlyArray<UserTemplate>): string | null {
    if (["id", "userId", "templateId", "parentId", "status", "createdAt", "updatedAt", "createdBy", "updatedBy",
        USER_IDENTITY_NAME_VIRTUAL_COLUMN_ID, USER_IDENTITY_EMAIL_VIRTUAL_COLUMN_ID, USER_IDENTITY_PHONE_NUMBER_VIRTUAL_COLUMN_ID].includes(id)) {return id;}
    const matches = templates.flatMap((template) => template.additionalColumns.filter((column) => column.id === id));
    const first = matches[0];
    if (first === undefined || matches.some((column) => column.displayName !== first.displayName || column.type !== first.type ||
        JSON.stringify(column.configuration) !== JSON.stringify(first.configuration))) {return null;}
    return id;
}

/** Preserve event precedence for unprefixed identifiers, but make parent
 * ownership explicit in the wire request. BTL labels remain deferred. */
export function nativeCsvEventColumn(getAdditionalColumns: CapabilityUdoColumnResolver, field: string, parent: OttopilotEventContainerTemplate, event?: OttopilotEventTemplate, amaron = false): string | null {
    if (amaron) {
        const bare = field.replace(/^eventContainer\./u, "");
        if ([AMARON__EVENT_CONTAINER__L1_DISPOSITION__COLUMN_ID, AMARON__EVENT_CONTAINER__L2_DISPOSITION__COLUMN_ID,
            AMARON__EVENT_CONTAINER__L3_DISPOSITION__COLUMN_ID, AMARON__EVENT_CONTAINER__L4_DISPOSITION__COLUMN_ID].some((id) => id === bare)) {
            field = event === undefined ? "disposition" : "eventContainer.disposition";
        } else if ([AMARON__EVENT__CALLS__L1_DISPOSITION__COLUMN_ID, AMARON__EVENT__CALLS__L2_DISPOSITION__COLUMN_ID,
            AMARON__EVENT__CALLS__L3_DISPOSITION__COLUMN_ID, AMARON__EVENT__CALLS__L4_DISPOSITION__COLUMN_ID].some((id) => id === bare)) {field = "disposition";}
    }
    const prefixed = field.startsWith("eventContainer.");
    let id = prefixed ? field.slice("eventContainer.".length) : field;
    const eventColumn = !prefixed ? getAdditionalColumns(event).find((column) => column.id === id) : undefined;
    const template = prefixed || event === undefined || eventColumn === undefined && ![
        "id", "templateId", "eventContainerId", "revisionId", "createdAt", "updatedAt", "createdBy", "updatedBy", "status", "notes", "disposition",
    ].includes(id) ? parent : event;
    if (id === "disposition" || amaron && template === event && String(event.id) === String(AmaronEventTypes.Calls) && id === AMARON__EVENT__CALLS__LAST_DISPOSITION__COLUMN_ID) {
        id = template.configuration.dispositionColumnConfig.columnId;
    }
    const column = getAdditionalColumns(template).find((item) => item.id === id);
    const fixed = ["id", "templateId", "revisionId", "createdAt", "updatedAt", "createdBy", "updatedBy", "status", "notes", "disposition"];
    if (template === event) {fixed.push("eventContainerId");}
    else {fixed.push("assignmentStatus");}
    if (column === undefined && !fixed.includes(id)) {return null;}
    if (id === "assignmentStatus" && !getAdditionalColumns(parent).some((item) => item.id === parent.configuration.eventContainersPage.leadOwnerColumnId)) {return null;}
    return event !== undefined && template === parent ? `eventContainer.${id}` : id;
}

/** Encode ordered request fields without cache normalization or credentials. */
export function buildNativeCsvDownloadUrl(args: {
    baseUrl: string;
    path: NativeCsvDownloadPath;
    request: NativeCsvRequestByPath[NativeCsvDownloadPath];
}): string {
    const selectedUserIds = "selectedIds" in args.request ? args.request.selectedIds : undefined;
    if (args.path === "/users/export" && Array.isArray(selectedUserIds) && selectedUserIds.length > CSV_DOWNLOAD_LIMITS.selectedUserIds) {
        throw new Error(`Select at most ${CSV_DOWNLOAD_LIMITS.selectedUserIds} users for this CSV export.`);
    }
    const parsed = validators[args.path].safeParse(args.request);
    if (!parsed.success) {throw new Error("Invalid CSV request. Check the current workspace, user, columns and filters.");}
    const groups = parsed.data.filters === undefined ? [] : [parsed.data.filters];
    while (groups.length > 0) {
        for (const item of groups.pop()!.filters) {
            if ("filters" in item) {groups.push(item);}
            else if (item.operator === "?") {throw new Error("Invalid CSV filter: this operation has no supported SQL translation.");}
        }
    }
    const bytes = new TextEncoder().encode(JSON.stringify(parsed.data));
    let binary = "";
    for (const byte of bytes) {binary += String.fromCharCode(byte);}
    const body = btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
    if (body.length > CSV_DOWNLOAD_LIMITS.encodedBodyBytes) {
        throw new Error("CSV request is too large. Reduce the selected filters or columns and retry.");
    }
    const url = new URL(`/sub-system/pluto${args.path}`, args.baseUrl);
    url.searchParams.set("body", body);
    const result = url.toString();
    if (!nativeCsvDownloadUrlIsWithinByteLimit(result)) {
        throw new Error("CSV download URL is too large for the gateway. Reduce the selected columns or filters and retry.");
    }
    return result;
}

/** A hidden browsing context leaves the application and visible tabs untouched.
 * Each request owns its frame so another click cannot cancel a pending export.
 * Keep frames until the document is unloaded: attachment responses have no
 * reliable completion/load event, and timed removal can abort a slow download.
 * Launching navigation does not prove download completion. */
export function launchNativeCsvDownload(url: string, targetDocument: Document = document): void {
    const frame = targetDocument.createElement("iframe");
    frame.hidden = true;
    frame.title = "CSV download";
    frame.referrerPolicy = "no-referrer";
    // Preserve request credentials, but do not let an unexpected error page
    // execute scripts, open popups or navigate the application.
    frame.setAttribute("sandbox", "allow-downloads allow-same-origin");
    frame.src = url;
    try {targetDocument.body.appendChild(frame);} catch (error) {frame.remove(); throw error;}
}
